import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from "react";

/**
 * TikTok-style animated gift overlay. Handles 6 animation variants:
 * float-up | burst | spin | fireworks | confetti | beam
 *
 * Usage:
 *   const fxRef = useRef();
 *   <GiftAnimation ref={fxRef} />
 *   // Later:
 *   fxRef.current.play({ emoji, animation, color, name, image_url, coins });
 *
 * Multiple plays queue and overlap (up to MAX_CONCURRENT).
 */

const DURATION_MS = {
    "float-up": 2600,
    "burst": 2400,
    "spin": 2600,
    "fireworks": 3200,
    "confetti": 3600,
    "beam": 3000,
};
const MAX_CONCURRENT = 6;

let _fxId = 0;

function Particle({ angle, distance, delay }) {
    const px = Math.cos(angle) * distance;
    const py = Math.sin(angle) * distance;
    return (
        <span
            className="wta-gift-particle"
            style={{ "--px": `${px}px`, "--py": `${py}px`, animationDelay: `${delay}ms` }}
        />
    );
}

function Confetti({ color }) {
    // 24 confetti pieces falling across the screen
    const pieces = Array.from({ length: 24 }, (_, i) => ({
        left: Math.random() * 100,
        delay: Math.random() * 600,
        rotate: Math.random() * 360,
        bg: i % 3 === 0 ? color : (i % 3 === 1 ? "#FFFFFF" : "#00E5FF"),
    }));
    return (
        <>
            {pieces.map((p, i) => (
                <span
                    key={i}
                    className="wta-confetti-piece"
                    style={{
                        left: `${p.left}%`,
                        background: p.bg,
                        animationDelay: `${p.delay}ms`,
                        transform: `rotate(${p.rotate}deg)`,
                    }}
                />
            ))}
        </>
    );
}

function GiftFx({ gift, onDone }) {
    const animation = gift.animation || "float-up";
    const duration = DURATION_MS[animation] || 2600;

    useEffect(() => {
        const t = setTimeout(onDone, duration + 80);
        return () => clearTimeout(t);
    }, [duration, onDone]);

    const style = {
        "--gift-glow": gift.color || "#FFD700",
        top: "50%",
        left: "50%",
    };

    // Position shift for concurrent animations so they don't overlap fully
    const offset = gift._offset || 0;
    if (offset) {
        style.left = `${50 + offset}%`;
    }

    const iconClass = `wta-gift-fx wta-gift-${animation}`;

    return (
        <div className={iconClass} style={style}>
            {animation === "confetti" && (
                <div className="absolute inset-0 pointer-events-none" style={{ position: "fixed", inset: 0 }}>
                    <Confetti color={gift.color || "#FFD700"} />
                </div>
            )}
            <div className="wta-gift-icon relative">
                {gift.image_url ? (
                    <img
                        src={gift.image_url}
                        alt=""
                        className="w-[180px] h-[180px] object-contain"
                        style={{ filter: `drop-shadow(0 0 28px ${gift.color || "#FFD700"})` }}
                    />
                ) : (
                    <span>{gift.emoji || "🎁"}</span>
                )}
                {animation === "fireworks" && (
                    <>
                        {Array.from({ length: 12 }).map((_, i) => (
                            <Particle
                                key={i}
                                angle={(i / 12) * Math.PI * 2}
                                distance={180 + Math.random() * 60}
                                delay={i * 25}
                            />
                        ))}
                    </>
                )}
            </div>
            {gift.name && (
                <div className="wta-gift-label">
                    {gift.name}
                    {gift.senderName ? <span className="ml-2 text-white/80">· @{gift.senderName}</span> : null}
                </div>
            )}
        </div>
    );
}

const GiftAnimation = forwardRef(function GiftAnimation(_props, ref) {
    const [queue, setQueue] = useState([]);
    const queueRef = useRef(queue);
    queueRef.current = queue;

    const play = useCallback((gift) => {
        if (!gift) return;
        setQueue((prev) => {
            if (prev.length >= MAX_CONCURRENT) return prev;
            const offset = prev.length === 0 ? 0 : (prev.length % 2 === 1 ? -14 : 14) * Math.ceil(prev.length / 2);
            return [...prev, { ...gift, _fxId: ++_fxId, _offset: offset }];
        });
    }, []);

    useImperativeHandle(ref, () => ({ play }), [play]);

    const remove = useCallback((id) => {
        setQueue((prev) => prev.filter((g) => g._fxId !== id));
    }, []);

    if (queue.length === 0) return null;

    return (
        <div data-testid="gift-animation-layer" className="pointer-events-none fixed inset-0 z-[70] overflow-hidden">
            {queue.map((g) => (
                <GiftFx key={g._fxId} gift={g} onDone={() => remove(g._fxId)} />
            ))}
        </div>
    );
});

export default GiftAnimation;
